/**
 * Communication tab of the legacy Config page.
 *
 * Parity target: Angular `config.component.html` `communicationTemplate`
 * (lines ~113–470). Covers email validation / whitelist, IMAP creds +
 * live Validate button, Mailgun domain, paid-reminder + active-defaults
 * dropdowns, and External Insurance toggle + email.
 *
 * Submits via POST /api/config with only the Communication-tab keys;
 * other tabs preserve their own values because the backend merges.
 */

import { forwardRef, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  buildCommunicationPayload,
  saveConfig,
  testImap,
} from '../../services/configService'
import type { CommunicationFormValues } from '../../types/config'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { SegmentedButtonGroup } from './formPrimitives'
import type {
  CommunicationTabInputProps,
  CommunicationTabProps,
  CommunicationTabReminderSelectProps,
} from '../../types'
import { SectionCard } from '@/components/ui/SectionCard'
import { SectionInfoIcon } from '@/components/ui/SectionInfoIcon'
import { COMM_FIELD_BASE, COMM_LABEL } from '@/lib/styles/companyEditTabShell'
import {
  TOOLTIP_REMINDER_FIRST,
  TOOLTIP_REMINDER_SECOND,
} from '@/features/company/constants'
import { cn } from '@/lib/utils/helpers'

const EMPTY_VALUES: CommunicationFormValues = {
  email_validation: '1',
  EMAIL_WHITELIST: '',
  IMAP_HOST: '',
  IMAP_PORT: '',
  IMAP_USERNAME: '',
  IMAP_PASSWORD: '',
  IMAP_STATUS: '',
  MAILGUN_DOMAIN: '',
  DEFAULT_FIRST_REMINDER: '',
  DEFAULT_SECOND_REMINDER: '',
  ACTIVE_DEFAULT_FIRST_REMINDER: '',
  ACTIVE_DEFAULT_SECOND_REMINDER: '',
  SEND_TO_EXTERNAL_INSURANCE: '',
  EXTERNAL_INSURANCE_EMAIL: '',
}

// Angular `emailFirst = Array(31).fill(0).map((x, i) => i + 1)` → 1..31
const REMINDER_FIRST = Array.from({ length: 31 }, (_, i) => i + 1)
// Angular `emailSecond = Array(24).fill(0).map((x, i) => i + 8)` → 8..31
const REMINDER_SECOND = Array.from({ length: 24 }, (_, i) => i + 8)

export function CommunicationTab({
  initialValues,
  isLoading,
  onSaved,
}: CommunicationTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const [imapTesting, setImapTesting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<CommunicationFormValues>({
    defaultValues: EMPTY_VALUES,
    mode: 'onChange',
  })

  useEffect(() => {
    if (initialValues) reset(initialValues)
  }, [initialValues, reset])

  const emailValidation = watch('email_validation')
  const imapStatus = watch('IMAP_STATUS')
  const sendExternalInsurance = watch('SEND_TO_EXTERNAL_INSURANCE')
  const imapHost = watch('IMAP_HOST')
  const imapPort = watch('IMAP_PORT')
  const imapUser = watch('IMAP_USERNAME')
  const imapPass = watch('IMAP_PASSWORD')
  const imapReady =
    (imapHost ?? '').trim() !== '' &&
    (imapPort ?? '').trim() !== '' &&
    (imapUser ?? '').trim() !== '' &&
    (imapPass ?? '').trim() !== ''

  const onSubmit = async (values: CommunicationFormValues) => {
    try {
      setSubmitLoading(true)
      await saveConfig(buildCommunicationPayload(values))
      showSuccess('Config Change Successfully!')
      reset(values)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleImapTest = async () => {
    try {
      setImapTesting(true)
      await testImap({
        IMAP_HOST: imapHost,
        IMAP_PORT: imapPort,
        IMAP_USERNAME: imapUser,
        IMAP_PASSWORD: imapPass,
      })
      showSuccess('Validate Successfully!')
    } catch (error) {
      showApiError(error)
    } finally {
      setImapTesting(false)
    }
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Email validation */}
      <SectionCard number={1} title="Email">
        <div className="flex flex-wrap gap-6 items-end">
          <div>
            <span className="block text-xs font-medium text-slate-600 mb-1">
              Email Validation
            </span>
            <SegmentedButtonGroup
              ariaLabel="Email validation"
              value={emailValidation ?? ''}
              onChange={(v) =>
                setValue('email_validation', v as CommunicationFormValues['email_validation'], {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              options={[
                { value: '1', label: 'Yes' },
                { value: '0', label: 'No' },
              ]}
            />
          </div>
          <div className="flex-1 min-w-[280px]">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Emails/Domains to skip validation (comma separated)
            </label>
            <input
              type="text"
              {...register('EMAIL_WHITELIST')}
              readOnly={emailValidation === '0'}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 read-only:bg-slate-100"
            />
          </div>
        </div>
      </SectionCard>

      {/* IMAP */}
      <SectionCard number={2} title="IMAP Email Parser">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <LabeledInput label="IMAP Host" {...register('IMAP_HOST')} />
          <LabeledInput label="IMAP Port" {...register('IMAP_PORT')} />
          <LabeledInput label="IMAP Email" {...register('IMAP_USERNAME')} />
          <LabeledInput
            label="IMAP Password"
            type="password"
            {...register('IMAP_PASSWORD')}
          />
        </div>
        <div className="flex flex-col gap-3 items-start">
          <button
            type="button"
            onClick={handleImapTest}
            disabled={!imapReady || imapTesting}
            className={primaryEntityActionButtonClass}
          >
            {imapTesting ? 'Validating…' : 'Validate'}
          </button>
          <div>
            <span className="block text-xs font-medium text-slate-600 mb-1">
              IMAP Status
            </span>
            <SegmentedButtonGroup
              ariaLabel="IMAP status"
              value={imapStatus ?? ''}
              onChange={(v) =>
                setValue('IMAP_STATUS', v as CommunicationFormValues['IMAP_STATUS'], {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              options={[
                { value: 'ON', label: 'ON' },
                { value: 'OFF', label: 'OFF' },
              ]}
            />
          </div>
        </div>
      </SectionCard>

      {/* Mailgun */}
      <SectionCard number={3} title="Mailgun">
        <div className="max-w-md">
          <LabeledInput label="Mailgun Domain" {...register('MAILGUN_DOMAIN')} />
        </div>
      </SectionCard>

      {/* Paid reminders — Angular config.component.html parity (field-level tooltips) */}
      <SectionCard number={4} title="Default Email">
        <p className="mb-4 text-sm font-bold text-slate-800">
          Resident Paid Reminder/Warning Email
        </p>
        <div className="grid w-full min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <ReminderSelect
            {...register('DEFAULT_FIRST_REMINDER')}
            label="First Reminder"
            options={REMINDER_FIRST}
            infoContent={TOOLTIP_REMINDER_FIRST}
            infoTestId="config-default-email-first-reminder"
          />
          <ReminderSelect
            {...register('DEFAULT_SECOND_REMINDER')}
            label="Second Reminder"
            options={REMINDER_SECOND}
            infoContent={TOOLTIP_REMINDER_SECOND}
            infoTestId="config-default-email-second-reminder"
          />
        </div>
      </SectionCard>

      {/* Active defaults */}
      <SectionCard number={5} title="List of Active Defaults Email Preferences">
        <div className="grid w-full min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <ReminderSelect
            {...register('ACTIVE_DEFAULT_FIRST_REMINDER')}
            label="First Reminder"
            options={REMINDER_FIRST}
            infoContent={TOOLTIP_REMINDER_FIRST}
            infoTestId="config-active-defaults-first-reminder"
          />
          <ReminderSelect
            {...register('ACTIVE_DEFAULT_SECOND_REMINDER')}
            label="Second Reminder"
            options={REMINDER_SECOND}
            infoContent={TOOLTIP_REMINDER_SECOND}
            infoTestId="config-active-defaults-second-reminder"
          />
        </div>
      </SectionCard>

      {/* External Insurance */}
      <SectionCard number={6} title="External Insurance">
        <div className="flex flex-wrap gap-6 items-end">
          <div>
            <span className="block text-xs font-medium text-slate-600 mb-1">
              Send to External Insurance
            </span>
            <SegmentedButtonGroup
              ariaLabel="Send to external insurance"
              value={sendExternalInsurance ?? ''}
              onChange={(v) =>
                setValue(
                  'SEND_TO_EXTERNAL_INSURANCE',
                  v as CommunicationFormValues['SEND_TO_EXTERNAL_INSURANCE'],
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  },
                )
              }
              options={[
                { value: 'YES', label: 'YES' },
                { value: 'NO', label: 'NO' },
              ]}
            />
          </div>
          <div className="flex-1 min-w-[280px]">
            <LabeledInput
              label="External Insurance Email"
              {...register('EXTERNAL_INSURANCE_EMAIL')}
            />
          </div>
        </div>
      </SectionCard>

      <div>
        <button
          type="submit"
          disabled={!isDirty || submitLoading || !isValid}
          className={primaryEntityActionButtonClass}
        >
          {submitLoading ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </form>
  )
}

const LabeledInput = forwardRef<HTMLInputElement, CommunicationTabInputProps>(
  ({ label, ...rest }, ref) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}
      </label>
      <input
        ref={ref}
        {...rest}
        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  ),
)
LabeledInput.displayName = 'LabeledInput'

const ReminderSelect = forwardRef<HTMLSelectElement, CommunicationTabReminderSelectProps>(
  ({ label, options, infoContent, infoTestId, className, id, ...rest }, ref) => {
    const fieldId = id ?? (typeof rest.name === 'string' ? `config-${rest.name}` : undefined)

    return (
      <div className="min-w-0 w-full">
        <div className={COMM_LABEL}>
          <label htmlFor={fieldId}>{label}</label>
          {infoContent != null ? (
            <SectionInfoIcon
              content={infoContent}
              data-testid={infoTestId}
              maxWidth={320}
              align="start"
            />
          ) : null}
        </div>
        <select
          id={fieldId}
          ref={ref}
          {...rest}
          className={cn(COMM_FIELD_BASE, 'mt-1.5 h-10 w-full', className)}
        >
          <option value="">Select Send Date</option>
          <option value="none">No Reminder</option>
          {options.map((n) => (
            <option key={n} value={String(n)}>
              {n}
            </option>
          ))}
        </select>
      </div>
    )
  },
)
ReminderSelect.displayName = 'ReminderSelect'
